import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { PromptGroup } from '../../interfaces/promptLibrary';
import { getPromptLibrary } from '../../services/promptLibraryService';
import ActionError from '../../utils/ActionError';

interface PromptLibraryState {
  prompts: PromptGroup[];
  loading: boolean;
  error: string | null;
}

const initialState: PromptLibraryState = {
  prompts: [],
  loading: false,
  error: null,
};

export const fetchPromptLibrary = createAsyncThunk<
  PromptGroup[],
  void,
  { rejectValue: string }
>('promptLibrary/fetchPromptLibrary', async (_, { rejectWithValue }) => {
  try {
    const data = await getPromptLibrary();
    return data;
  } catch (err) {
    return rejectWithValue(
      (err as ActionError).message || 'Failed to fetch prompt library'
    );
  }
});

const promptLibrarySlice = createSlice({
  name: 'promptLibrary',
  initialState,
  reducers: {
    resetPromptLibrary: () => {
      return initialState;
    },
    setPrompts: (state, action: PayloadAction<PromptGroup[]>) => {
      state.prompts = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPromptLibrary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromptLibrary.fulfilled, (state, action) => {
        state.loading = false;
        state.prompts = action.payload;
      })
      .addCase(fetchPromptLibrary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch prompt library';
      });
  },
});

export const { resetPromptLibrary, setPrompts, setLoading, setError } =
  promptLibrarySlice.actions;
export default promptLibrarySlice.reducer;
